import {
  ENABLED_RELEASE_STAGES,
  MONITORING_REDACTED_KEYS,
  isNonActionableError,
  sanitizeUrl,
  sanitizeUrlsDeep,
} from '@point2-inc/constants';
import { monitoringEnv } from './env';

/**
 * The half of Web's Bugsnag setup that both runtimes share.
 *
 * Kept apart from the clients so that neither runtime imports the other's SDK.
 * Nothing in here touches `window`, `document` or any Node built-in.
 */

/** Narrowed to what `onError` touches, so neither SDK's types are needed. */
export interface BugsnagEventLike {
  context?: string;
  errors?: Array<{ errorClass?: string; errorMessage?: string }>;
  originalError?: { name?: string; message?: string; code?: string };
  breadcrumbs?: Array<{ metadata?: Record<string, unknown> }>;
  request?: { url?: string };
}

/**
 * Waitlist links carry their secret in the URL fragment (`/waitlist/verify#t=…`).
 * The page strips it on mount, but Bugsnag starts first: its navigation
 * breadcrumbs record `from`/`to` with the fragment for Next's first
 * `history.replaceState`, and `sanitizeUrl` (shared with the apps) only knows
 * path and query secrets. So any `#t=` value is redacted here, everywhere.
 */
const FRAGMENT_SECRET = /#t=[^\s"'&#]*/g;

export const scrubFragmentSecret = (value: string): string => value.replace(FRAGMENT_SECRET, '#t=[redacted]');

function scrubDeep<T>(value: T, depth = 0): T {
  if (depth > 6) return value;
  if (typeof value === 'string') return scrubFragmentSecret(value) as T;
  if (Array.isArray(value)) return value.map((v) => scrubDeep(v, depth + 1)) as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = scrubDeep(v, depth + 1);
    return out as T;
  }
  return value;
}

export function shouldDiscard(event: BugsnagEventLike): boolean {
  const first = event.errors?.[0];
  const original = event.originalError;
  return isNonActionableError(
    [original?.name, original?.message, original?.code, first?.errorClass, first?.errorMessage]
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
  );
}

/**
 * Strips the shared-trip token out of anything on its way to the dashboard.
 *
 * This is the reason Web's redaction cannot stop at `redactedKeys`. The public
 * shared-trip page is `/trip/share/<token>`, and that token is the *entire*
 * authentication for someone's live journey — their position, their route,
 * their destination. It is not a value under a key called `token`; it is a path
 * segment, and it turns up in `event.context` (Bugsnag sets that from the URL),
 * in a message, and in a breadcrumb's metadata. `redactedKeys` matches key
 * names and would not touch any of them.
 *
 * `/trip/share/[redacted]` is kept rather than dropping the URL entirely: the
 * route still identifies the page that broke.
 */
export function sanitizeEvent(event: BugsnagEventLike): void {
  try {
    if (typeof event.context === 'string') event.context = scrubFragmentSecret(sanitizeUrl(event.context));
    for (const err of event.errors ?? []) {
      if (typeof err.errorMessage === 'string') {
        err.errorMessage = scrubFragmentSecret(sanitizeUrl(err.errorMessage));
      }
    }
    for (const crumb of event.breadcrumbs ?? []) {
      if (crumb.metadata) crumb.metadata = scrubDeep(sanitizeUrlsDeep(crumb.metadata));
    }
    if (event.request && typeof event.request.url === 'string') {
      event.request.url = scrubFragmentSecret(sanitizeUrl(event.request.url));
    }
  } catch {
    // Never lose an event over sanitising it.
  }
}

export function baseConfig(apiKey: string, appType: 'browser' | 'server') {
  return {
    apiKey,
    releaseStage: monitoringEnv.tier,
    // Every tier transmits; the dashboard tells them apart by releaseStage.
    enabledReleaseStages: [...ENABLED_RELEASE_STAGES],
    appType,
    appVersion: monitoringEnv.appVersion,
    autoTrackSessions: true,
    maxBreadcrumbs: 40,
    redactedKeys: [...MONITORING_REDACTED_KEYS],
    onError: (event: BugsnagEventLike) => {
      if (shouldDiscard(event)) return false;
      sanitizeEvent(event);
      return true;
    },
  };
}

/**
 * Whether a Next.js error has already been reported by the server.
 *
 * A render error on the server reaches Bugsnag once through
 * `instrumentation.ts`'s `onRequestError`, then reaches the browser boundary a
 * second time as a sanitised `Error` carrying a `digest`. Reporting both files
 * one incident twice, and the client copy is the one without the stack.
 */
/** `sanitizeUrlsDeep`, under a name that says why the server uses it. */
export const sanitizeUrlsForServer = <T,>(value: T): T => scrubDeep(sanitizeUrlsDeep(value));

export const wasReportedOnServer = (error: { digest?: string }): boolean =>
  typeof error.digest === 'string' && error.digest.length > 0;

export { sanitizeUrl };
